import { FC, useState, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Button, Modal, Stack } from '@servicetitan/design-system';
import classnames from 'classnames';

const connectionErrorSvg = require('../assets/connection-error.svg');
const unknownErrorSvg = require('../assets/unknown-error.svg');

interface ErrorDialogData {
    image: string;
    imageAlt: string;
    title: string;
    message: JSX.Element;
    refresh: string;
}

export const ErrorDialog: FC = observer(() => {
    const [open, setOpen] = useState(false);
    const [isConnectionError, setConnectionError] = useState(false);

    const close = () => setOpen(false);

    useEffect(() => {
        const listener = (event: CustomEvent) => {
            setOpen(true);
            setConnectionError(!!event.detail.isConnectionError);
        };

        window.addEventListener('error_dialog', listener);

        return () => {
            window.removeEventListener('error_dialog', listener);
        };
    }, []);

    const toSupport = useCallback((e: any) => {
        e.preventDefault();
        close();
        window.dispatchEvent(
            new CustomEvent('toggleHelpWidget', {
                detail: {
                    route: '/support',
                },
            })
        );
    }, []);

    const back = () => {
        close();
        history.back();
    };

    const reload = () => {
        close();
        location.reload();
    };

    const hasPreviousPage = window.history.length > 1;

    let data: ErrorDialogData;
    if (isConnectionError) {
        data = {
            image: connectionErrorSvg,
            imageAlt: 'Connection error',
            title: 'There was a server connection error',
            message: (
                <p className="m-t-0 m-b-3 ta-center">
                    Please refresh this page, or try again later.
                    <br /> If the issue persists,{' '}
                    <a className="c-blue-400-i" href="#" onClick={toSupport}>
                        contact
                    </a>{' '}
                    our support team.
                </p>
            ),
            refresh: 'Refresh the page',
        };
    } else {
        data = {
            image: unknownErrorSvg,
            imageAlt: 'Something went wrong',
            title: 'Something went wrong here',
            message: (
                <p className="m-t-0 m-b-3">
                    Try reloading the page or{' '}
                    <a className="c-blue-400-i" href="#" onClick={toSupport}>
                        contact
                    </a>{' '}
                    support if the issue persists.
                </p>
            ),
            refresh: 'Reload the page',
        };
    }

    return (
        <Modal
            open={open}
            size={Modal.Sizes.S}
            title={data.title}
            closable
            onClose={close}
            footer={
                <Stack alignItems="flex-end" spacing={1}>
                    {hasPreviousPage && (
                        <Button primary fill="subtle" onClick={back}>
                            Go back
                        </Button>
                    )}
                    <Button primary outline onClick={reload}>
                        {data.refresh}
                    </Button>
                </Stack>
            }
        >
            <Stack direction="column" alignItems="center" justifyContent="center">
                <img
                    src={data.image}
                    alt={data.imageAlt}
                    className={classnames('m-y-4', 'c-neutral-100')}
                />
                {data.message}
            </Stack>
        </Modal>
    );
});
